<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </Teleport>

  <div v-if="visible" class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h2 class="modal-title">Apply to Another Template</h2>
        <button class="close-btn" @click="handleClose" type="button">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">
            <span class="required">*</span> Target HomeMetric Care Plan
          </label>
          <div class="custom-select" ref="planSelectRef">
            <div
              class="select-trigger"
              :class="{
                'has-placeholder': !selectedPlan,
                error: showValidationError && !selectedPlan,
                open: planDropdownOpen,
              }"
              @click="planDropdownOpen = !planDropdownOpen"
            >
              <span class="select-value">{{ planDisplayValue }}</span>
              <img :src="downArrowIcon" alt="arrow" class="select-arrow" />
              <img
                v-if="showValidationError && !selectedPlan"
                :src="alertIcon"
                alt="error"
                class="error-icon"
              />
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label"> <span class="required">*</span> Target Template </label>
          <div class="custom-select" ref="templateSelectRef">
            <div
              class="select-trigger"
              :class="{
                'has-placeholder': !selectedTemplateId,
                error: showValidationError && !selectedTemplateId,
                disabled: !selectedPlan,
                open: templateDropdownOpen,
              }"
              @click="!selectedPlan || (templateDropdownOpen = !templateDropdownOpen)"
            >
              <span class="select-value">{{ templateDisplayValue }}</span>
              <img :src="downArrowIcon" alt="arrow" class="select-arrow" />
              <img
                v-if="showValidationError && !selectedTemplateId"
                :src="alertIcon"
                alt="error"
                class="error-icon"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="handleClose" type="button">Cancel</button>
        <button class="btn-submit" @click="handleApply" type="button" :disabled="submitting">
          Apply
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="visible && planDropdownOpen"
      class="dropdown-backdrop"
      @click.self="planDropdownOpen = false"
    >
      <div class="select-dropdown" :style="planDropdownStyle">
        <div v-if="loadingPlans" class="select-empty select-empty--loading">
          <span class="dropdown-spinner" aria-hidden="true"></span>
        </div>
        <template v-else>
          <div
            v-for="option in planOptions"
            :key="option.value"
            class="select-option"
            :class="{ selected: option.value === selectedPlan }"
            @click="selectPlan(option.value)"
          >
            {{ option.label }}
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="visible && templateDropdownOpen"
      class="dropdown-backdrop"
      @click.self="templateDropdownOpen = false"
    >
      <div class="select-dropdown" :style="templateDropdownStyle">
        <div v-if="loadingTemplates" class="select-empty select-empty--loading">
          <span class="dropdown-spinner" aria-hidden="true"></span>
        </div>
        <template v-else-if="templateOptions.length > 0">
          <div
            v-for="option in templateOptions"
            :key="option.value"
            class="select-option"
            :class="{ selected: option.value === selectedTemplateId }"
            @click="selectTemplate(option.value)"
          >
            {{ option.label }}
          </div>
        </template>
        <div v-else class="select-empty">No available templates</div>
      </div>
    </div>
  </Teleport>

  <div v-if="showConfirmModal" class="modal-overlay">
    <div class="confirm-container">
      <div class="confirm-header">
        <h2 class="confirm-title">Confirm Replacement</h2>
        <button class="close-btn" @click="closeConfirm" type="button">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="confirm-body">
        <p>
          This will apply the current items to the selected template. Existing items in the target
          template will be replaced.
        </p>
        <p>Are you sure you want to continue?</p>
      </div>

      <div class="confirm-footer">
        <button class="btn-no" @click="closeConfirm" type="button">No</button>
        <button class="btn-confirm" @click="confirmApply" type="button">Confirm</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {
  getCareTemplateList,
  getCareTemplateMeta,
  applyTemplate,
  normalizeCareTemplateMetaPlan,
  type CareTemplate,
  type ApplyTemplateResponse,
  type CareTemplateMetaPlan,
} from '@/api/ApplyPremium'
import ToastComponent from '@/components/ToastComponent.vue'
import closeIcon from '@/assets/Fill 1@2x.png'
import downArrowIcon from '@/assets/downarrow@2x.png'
import alertIcon from '@/assets/alert.png'

interface Props {
  visible: boolean
  templateId: number | null
  planKey: string
}

interface PlanOption {
  label: string
  value: string
}

interface TemplateOption {
  label: string
  value: number
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const selectedPlan = ref('')
const selectedTemplateId = ref<number | null>(null)
const submitting = ref(false)
const showConfirmModal = ref(false)
const showValidationError = ref(false)
const planDropdownOpen = ref(false)
const templateDropdownOpen = ref(false)
const planSelectRef = ref<HTMLElement | null>(null)
const templateSelectRef = ref<HTMLElement | null>(null)

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null

const availableTemplates = ref<CareTemplate[]>([])
const loadingTemplates = ref(false)
const loadingPlans = ref(false)
const planOptions = ref<PlanOption[]>([])

interface PlanMeta {
  plan_key: string
  plan_text?: string
  label?: string
}

async function fetchPlanOptions(): Promise<void> {
  loadingPlans.value = true
  try {
    const response = await getCareTemplateMeta()
    console.log('Meta API response:', response)

    // 检查响应是否成功
    if (response.code !== 200) {
      console.warn('Meta API returned non-200 code:', response.code)
      // 如果接口失败，使用默认选项作为fallback
      planOptions.value = [
        { label: 'Essential Care', value: 'essential_care' },
        { label: 'Enhanced Care', value: 'enhanced_care' },
        { label: 'Premium Care', value: 'premium_care' },
      ]
      return
    }

    if (!response.data) {
      console.warn('Meta API returned empty data')
      planOptions.value = [
        { label: 'Essential Care', value: 'essential_care' },
        { label: 'Enhanced Care', value: 'enhanced_care' },
        { label: 'Premium Care', value: 'premium_care' },
      ]
      return
    }

    // 支持多种数据结构
    if (response.data.plans && Array.isArray(response.data.plans)) {
      planOptions.value = response.data.plans
        .map((plan: CareTemplateMetaPlan) => normalizeCareTemplateMetaPlan(plan))
        .filter((opt) => opt.value)
    } else if (Array.isArray(response.data)) {
      // 如果后端直接返回数组
      planOptions.value = (response.data as PlanMeta[])
        .map((item) => normalizeCareTemplateMetaPlan(item))
        .filter((opt) => opt.value)
    } else {
      console.warn('Unsupported data structure:', typeof response.data)
      // Fallback to default options
      planOptions.value = [
        { label: 'Essential Care', value: 'essential_care' },
        { label: 'Enhanced Care', value: 'enhanced_care' },
        { label: 'Premium Care', value: 'premium_care' },
      ]
    }
  } catch (error) {
    console.error('Failed to fetch plan options:', error)
    // 网络错误时使用默认选项
    planOptions.value = [
      { label: 'Essential Care', value: 'essential_care' },
      { label: 'Enhanced Care', value: 'enhanced_care' },
      { label: 'Premium Care', value: 'premium_care' },
    ]
  } finally {
    loadingPlans.value = false
  }
}

const templateOptions = computed<TemplateOption[]>(() => {
  const seen = new Set<string>()
  return availableTemplates.value
    .map((t) => ({
      label: getTemplateLabel(t),
      value: t.id,
    }))
    .filter((opt) => {
      if (seen.has(opt.label)) return false
      seen.add(opt.label)
      return true
    })
})

function getTemplateLabel(template: CareTemplate): string {
  if (template.plan_key === 'premium_care') {
    if (template.month) {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ]
      return `${template.template_name} - ${monthNames[template.month - 1]}`
    }
    return template.template_name
  }

  if (template.start_month && template.end_month) {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]
    const startName = monthNames[template.start_month - 1]
    const endName = monthNames[template.end_month - 1]
    return `${startName}-${endName}`
  }

  return template.template_name
}

const planDisplayValue = computed(() => {
  if (!selectedPlan.value) {
    return 'Select HomeMetric Care Plan'
  }
  const option = planOptions.value.find((opt) => opt.value === selectedPlan.value)
  return option ? option.label : 'Select HomeMetric Care Plan'
})

const templateDisplayValue = computed(() => {
  if (!selectedTemplateId.value) {
    return 'Select template'
  }
  const option = templateOptions.value.find((opt) => opt.value === selectedTemplateId.value)
  return option ? option.label : 'Select template'
})

const planDropdownStyle = computed(() => {
  if (!planSelectRef.value || !planDropdownOpen.value) return {}
  const rect = planSelectRef.value.getBoundingClientRect()
  return {
    position: 'fixed' as const,
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
})

const templateDropdownStyle = computed(() => {
  if (!templateSelectRef.value || !templateDropdownOpen.value) return {}
  const rect = templateSelectRef.value.getBoundingClientRect()
  return {
    position: 'fixed' as const,
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
  }, 3500)
}

async function fetchTemplates(): Promise<void> {
  if (!selectedPlan.value) {
    availableTemplates.value = []
    return
  }

  loadingTemplates.value = true
  availableTemplates.value = []

  try {
    const response = await getCareTemplateList({
      plan_key: selectedPlan.value,
    })

    if (response.code === 200 && response.data && response.data.list) {
      availableTemplates.value = response.data.list.filter((t) => t.id !== props.templateId)
    } else {
      availableTemplates.value = []
    }
  } catch {
    availableTemplates.value = []
  } finally {
    loadingTemplates.value = false
  }
}

function selectPlan(value: string): void {
  selectedPlan.value = value
  selectedTemplateId.value = null
  planDropdownOpen.value = false
  void fetchTemplates()
}

function selectTemplate(value: number): void {
  selectedTemplateId.value = value
  templateDropdownOpen.value = false
}

function handleClickOutside(event: MouseEvent): void {
  if (planSelectRef.value && !planSelectRef.value.contains(event.target as Node)) {
    planDropdownOpen.value = false
  }
  if (templateSelectRef.value && !templateSelectRef.value.contains(event.target as Node)) {
    templateDropdownOpen.value = false
  }
}

function handleApply(): void {
  if (!selectedPlan.value || !selectedTemplateId.value) {
    showValidationError.value = true
    showToast('warning', 'Please complete all required fields.')
    return
  }

  showConfirmModal.value = true
}

async function confirmApply(): Promise<void> {
  closeConfirm()
  submitting.value = true

  try {
    const response: ApplyTemplateResponse = await applyTemplate({
      source_template_id: props.templateId!,
      target_template_id: selectedTemplateId.value!,
      confirm_replace: 1,
    })

    if (response.code === 200) {
      emit('saved')
      emit('close') // 立即关闭弹窗
      showToast('success', 'Successfully applied') // 弹窗关闭后显示toast
    } else if (response.code === 409 && response.data?.need_confirm_replace) {
      showToast(
        'warning',
        response.msg || 'Target template already has items. Please confirm replacement.',
      )
      showConfirmModal.value = false
    } else {
      showToast('error', response.msg || 'Failed to apply')
    }
  } catch {
    showToast('error', 'Failed to apply')
  } finally {
    submitting.value = false
  }
}

function closeConfirm(): void {
  showConfirmModal.value = false
}

function handleClose(): void {
  selectedPlan.value = ''
  selectedTemplateId.value = null
  availableTemplates.value = []
  showValidationError.value = false
  planDropdownOpen.value = false
  templateDropdownOpen.value = false
  emit('close')
}

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      void fetchPlanOptions()
    } else {
      selectedPlan.value = ''
      selectedTemplateId.value = null
      availableTemplates.value = []
      showValidationError.value = false
      planDropdownOpen.value = false
      templateDropdownOpen.value = false
    }
  },
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 16px;
  max-width: 530px;
  width: 90%;
  min-height: 296px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
@media (max-width: 576px) {
  .modal-container {
    max-width: 100%;
    height: auto;
  }
}

.confirm-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 530px;
  height: 212px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.modal-header,
.confirm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
}

.modal-title,
.confirm-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  padding: 16px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ✅ 修复Confirm弹窗多余空白行 */
.confirm-body {
  flex: 1; /* 让内容区域自动填充剩余空间 */
  padding: 24px 24px 0; /* 移除底部内边距，全部交给footer */
  font-size: 14px;
  color: #000000;
  line-height: 1.6;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  text-align: left;
  box-sizing: border-box;
}

.confirm-body p {
  margin: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.required {
  color: #ff4d4f;
  margin-right: 4px;
}

.custom-select {
  position: relative;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 50px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
  width: 482px;
  height: 32px;
  box-sizing: border-box;
  position: relative;
}

.select-trigger.has-placeholder {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.select-trigger:not(.has-placeholder) {
  color: #000000;
}

.select-trigger:not(.has-placeholder) .select-value {
  color: #000000;
}

.select-trigger.open:not(.disabled),
.select-trigger:focus-within:not(.disabled):not(.error) {
  border-color: #666666;
}

.select-trigger.error {
  border-color: #ff4d4f;
  background: #fffafa;
}

.select-trigger.disabled {
  background-color: #f5f6fb;
  cursor: not-allowed;
  color: #999;
}

.select-value {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-arrow {
  width: 16px;
  height: 16px;
  max-width: 16px;
  max-height: 16px;
  object-fit: contain;
  opacity: 0.55;
  transition: transform 0.2s;
  flex-shrink: 0;
  position: absolute;
  right: 16px;
}

.error-icon {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

.select-trigger.open .select-arrow {
  transform: rotate(180deg);
}

/* 保留Teleport下拉框的遮罩样式 */
.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
}

/* ✅ 完全对齐截图的下拉框样式 */
.select-dropdown {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  box-sizing: border-box;
}

.select-option {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  line-height: 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

/* ✅ 对齐截图的悬停颜色（浅灰色背景） */
.select-option:hover:not(.disabled) {
  background-color: #f5f5f5;
}

.select-option.selected {
  background-color: transparent;
  color: #000000;
}

.select-option.disabled {
  color: #ccc;
  cursor: not-allowed;
  background-color: #fafafa;
}

/* 保留空状态样式 */
.select-empty {
  padding: 12px 16px;
  color: #999;
  font-size: 14px;
  text-align: center;
  border-radius: 12px;
}

.select-empty--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 12px 16px;
}

.dropdown-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #00aeef;
  border-radius: 50%;
  animation: dropdown-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes dropdown-spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 0 24px 24px;
  flex: 1;
}

/* ✅ 修复Confirm弹窗按钮间距和对齐 */
.confirm-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center; /* 改为垂直居中，不再靠下 */
  gap: 16px;
  padding: 0 24px 24px; /* 统一使用padding，和主弹窗一致 */
  flex-shrink: 0;
}

.btn-cancel {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 16px;
  background-color: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-no {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 16px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover,
.btn-no:hover {
  background-color: #000000;
  color: #ffffff;
}

.btn-submit {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-confirm {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-submit:hover:not(:disabled),
.btn-confirm:hover {
  background-color: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
